/**
 * اموال (v2.147.0) — فهرستِ دارایی‌های کارخانه: ملکِ خودمان یا امانی نزدِ ما.
 *
 * کاربر (۱۴۰۵/۰۷/۱۴): «بخشِ اموال با دسترسیِ انحصاری؛ ردیف، نام، ویژگی، مالک (خودمان یا امانی)، سریال، کد،
 * تعداد، مبلغِ حدودی، تاریخِ خرید و ورود، محلِ نگهداری؛ افزودن/ویرایش/ویرایشِ دسته‌جمعی/خروجی/مرتب‌سازی؛
 * تیک زدن و برای انتخاب‌شده‌ها چاپ، خروجی و لیبل — همهٔ دکمه‌ها بالای صفحه، نه انتهای آن».
 * v2.148.0: «سریالِ لیبلِ خودِ دستگاه را با دوربینِ گوشی همان‌جا اسکن کنیم؛ دستی هم بشود؛ روی گوشی راحت و بهینه».
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Checkbox, Col, Dropdown, Form, Grid, Input, InputNumber, Modal, Radio, Row, Segmented, Select,
  Space, Statistic, Tag, Tooltip, Typography, message, Popconfirm,
} from "antd";
import {
  PlusOutlined, PrinterOutlined, TagsOutlined, DeleteOutlined, EditOutlined, CopyOutlined,
  ReloadOutlined, CloseCircleOutlined, ScanOutlined, MoreOutlined, FileExcelOutlined, CameraFilled,
} from "@ant-design/icons";
import BarcodeScanner from "../components/BarcodeScanner";
import AssetPhotoField, { AssetPhotoPreview } from "../components/AssetPhoto";
import { downloadTableXlsx } from "../api/client";
import { tableToPayload } from "../utils/tableExport";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import EditableTable from "../components/EditableTable";
import ExportButton from "../components/ExportButton";
import { JalaliDateInput, toJalali } from "../utils/dateUtils";
import { formatMoney, moneyCell, MoneyInput } from "../utils/money";
import { faNum } from "../utils/faNumberFormat";
import { byDate, byNum, byStr, byCode } from "../utils/sorters";
import { LABEL_FIELDS, LABEL_SIZES, ownerText, printAssetLabels, printAssetList } from "../utils/assetPrint";
import { loadOrder, saveOrder } from "../utils/layoutStore";

const { Title, Text } = Typography;
const BASE = "/api/v1/assets";
const OWNERSHIP_OPTS = [
  { value: "own", label: "مالک: خودمان" },
  { value: "custody", label: "امانی (مالک دیگری)" },
];
const STATUS_OPTS = ["سالم", "نیازمند تعمیر", "خراب", "از رده خارج", "فروخته‌شده", "برگشت به مالک"]
  .map((v) => ({ value: v, label: v }));
const errText = (e, fb = "خطا") => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};
const norm = (s) => String(s ?? "").replace(/ي/g, "ی").replace(/ك/g, "ک").toLowerCase();

/** گزینه‌های پیشنهادی از مقادیرِ موجودِ یک ستون (برای Select با ورودیِ آزاد) */
const distinct = (rows, key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))]
  .sort((a, b) => String(a).localeCompare(String(b), "fa")).map((v) => ({ value: v, label: v }));

const LABEL_PREF = "assets-label";

export default function AssetsPage() {
  const { can } = useAuth();
  const canEdit = can("assets.edit");
  const canDelete = can("assets.delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [own, setOwn] = useState(() => loadOrder("asf", "assets") || "all");
  const [modal, setModal] = useState({ open: false, row: null });
  const [labelOpen, setLabelOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isMobile = !Grid.useBreakpoint().md;
  // اسکنر: "serial" = پرکردنِ سریالِ فرم | "search" = پیداکردنِ اموال از روی لیبل
  const [scanFor, setScanFor] = useState(null);
  // عکسِ سریال در فرم: { file?, origBytes?, remove? } — بعد از ذخیرهٔ ردیف ارسال می‌شود
  const [photo, setPhoto] = useState({});
  const [previewAsset, setPreviewAsset] = useState(null);
  const serialVal = Form.useWatch("serial_no", form);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get(`${BASE}/`)).data || []); }
    catch (e) { message.error(errText(e, "خطا در بارگذاریِ اموال")); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { saveOrder("asf", "assets", own); }, [own]);

  const view = useMemo(() => {
    const t = norm(q.trim());
    return rows.filter((r) => (own === "all" || r.ownership === own)
      && (!t || norm([r.code, r.serial_no, r.name, r.category, r.specs, r.owner_name, r.location,
        r.custodian, r.status, r.notes].join(" ")).includes(t)));
  }, [rows, q, own]);

  // «ردیف» = جایگاه در کلِ فهرست (به ترتیبِ ثبت) — با فیلتر/صفحه عوض نمی‌شود
  const rowNo = useMemo(() => new Map(rows.map((r, i) => [r.id, i + 1])), [rows]);

  // انتخاب‌ها به ترتیبِ لیستِ فعلی (فیلترشده)؛ اگر چیزی تیک نخورده ⇒ همهٔ لیستِ فعلی
  const selSet = useMemo(() => new Set(selected.map(String)), [selected]);
  const selectedRows = useMemo(() => rows.filter((r) => selSet.has(String(r.id))), [rows, selSet]);
  const target = selectedRows.length ? selectedRows : view;
  const targetLabel = selectedRows.length ? `${faNum(selectedRows.length)} انتخاب‌شده` : `همهٔ ${faNum(view.length)} ردیفِ فعلی`;

  const opts = useMemo(() => ({
    category: distinct(rows, "category"), location: distinct(rows, "location"),
    owner_name: distinct(rows, "owner_name"), custodian: distinct(rows, "custodian"),
    status: [...STATUS_OPTS, ...distinct(rows, "status").filter((o) => !STATUS_OPTS.some((s) => s.value === o.value))],
  }), [rows]);

  // ─────────────── افزودن / ویرایشِ تکی ───────────────
  const openNew = async (copyFrom = null) => {
    form.resetFields();
    let code = "";
    try { code = (await api.get(`${BASE}/next-code`)).data.code; } catch { /* خالی ⇒ سرور خودش می‌سازد */ }
    const base = copyFrom ? { ...copyFrom, serial_no: null } : { ownership: "own", quantity: 1 };
    delete base.id;
    form.setFieldsValue({ ...base, code });
    setPhoto({});
    setModal({ open: true, row: null });
  };
  const openEdit = (r) => { form.resetFields(); form.setFieldsValue(r); setPhoto({}); setModal({ open: true, row: r }); };

  /** عکسِ فرم را بعد از ذخیرهٔ ردیف می‌فرستد؛ خطای عکس ردیف را خراب نمی‌کند (پیامِ جدا) */
  const syncPhoto = async (assetId) => {
    try {
      if (photo.file) {
        const fd = new FormData(); fd.append("file", photo.file, "photo.jpg");
        const r = (await api.post(`${BASE}/${assetId}/photo`, fd)).data;
        message.success(`عکس ذخیره شد (${Math.round(r.photo_bytes / 1024).toLocaleString("fa-IR")} KB)`);
      } else if (photo.remove) {
        await api.delete(`${BASE}/${assetId}/photo`);
      }
    } catch (e) { message.error(`ردیف ذخیره شد ولی عکس نه: ${errText(e)}`); }
  };
  /** next=true ⇒ «ثبت و بعدی»: فرم برای قلمِ بعدی باز می‌ماند و محل/مالک/تاریخ‌ها حفظ می‌شوند (ثبتِ پشتِ‌سرِهم روی گوشی) */
  const submit = async (next = false) => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      if (modal.row) {
        await api.patch(`${BASE}/${modal.row.id}`, v);
        await syncPhoto(modal.row.id);
      } else {
        const r = (await api.post(`${BASE}/`, v)).data;
        message.success(`«${r.name}» با کدِ ${r.code} ثبت شد`);
        await syncPhoto(r.id);
      }
      setPhoto({});
      if (next && !modal.row) {
        let code = "";
        try { code = (await api.get(`${BASE}/next-code`)).data.code; } catch { /* */ }
        const keep = ["ownership", "owner_name", "category", "location", "custodian", "status",
          "purchase_date", "entry_date"].reduce((o, k) => ({ ...o, [k]: v[k] }), {});
        form.resetFields();
        form.setFieldsValue({ ...keep, quantity: 1, code });
      } else {
        setModal({ open: false, row: null });
      }
      load();
    } catch (e) { message.error(errText(e)); }
    setSaving(false);
  };

  const removeMany = async (ids) => {
    const r = (await api.post(`${BASE}/bulk-delete`, { ids: ids.map(Number) })).data;
    message.success(`${faNum(r.deleted)} ردیف حذف شد`);
    setSelected((s) => s.filter((k) => !ids.map(String).includes(String(k))));
    load();
  };

  // سریالِ تکراری فقط هشدار است (دو دستگاهِ یکسان از دو مالک ممکن است)، نه مانع
  const serialDup = useMemo(() => {
    const v = norm(String(serialVal || "").trim());
    if (!v) return null;
    return rows.find((r) => norm(r.serial_no) === v && r.id !== modal.row?.id) || null;
  }, [serialVal, rows, modal.row]);

  const onScanned = (val) => {
    if (scanFor === "serial") {
      form.setFieldsValue({ serial_no: val });
      message.success(`سریال نشست: ${val}`);
      return;
    }
    if (scanFor === "code") {
      const v = val.toUpperCase();
      const dup = rows.find((r) => norm(r.code) === norm(v) && r.id !== modal.row?.id);
      form.setFieldsValue({ code: v });
      if (dup) message.warning(`کدِ ${v} قبلاً برای «${dup.name}» ثبت شده — ذخیره رد می‌شود`);
      else message.success(`کد نشست: ${v}`);
      return;
    }
    // جست‌وجو: کدِ لیبلِ خودمان (AM-…) یا سریالِ دستگاه
    const t = norm(val);
    const hit = rows.filter((r) => norm(r.code) === t || norm(r.serial_no) === t);
    setOwn("all");
    setQ(val);
    if (hit.length === 1) {
      message.success(`پیدا شد: ${hit[0].code} — ${hit[0].name}`);
      if (canEdit) openEdit(hit[0]);
    } else if (!hit.length) {
      message.warning("اموالی با این کد/سریال ثبت نشده");
    }
  };

  // ─────────────── ستون‌ها ───────────────
  const columns = useMemo(() => {
    const free = (key) => ({ editType: "select", editAllowNew: true, options: opts[key] });
    return [
      { title: "ردیف", key: "__row", width: 60, gxFilter: false, gxExport: false,
        render: (_, r) => faNum(rowNo.get(r.id) || "") },
      { title: "کد اموال", dataIndex: "code", width: 105, sorter: byCode("code"), editType: "text",
        editRule: (v) => (!v ? "کد لازم است" : null),
        render: (v) => <Text strong style={{ direction: "ltr", display: "inline-block" }}>{v}</Text> },
      { title: "نام", dataIndex: "name", width: 180, sorter: byStr("name"), editType: "text",
        editRule: (v) => (!v ? "نام لازم است" : null) },
      { title: "ویژگی‌ها / مشخصات", dataIndex: "specs", width: 220, sorter: byStr("specs"), editType: "textarea",
        render: (v) => v || "—" },
      { title: "مالکیت", dataIndex: "ownership", width: 105, sorter: byStr("ownership"),
        editType: "select", editAllowClear: false, options: OWNERSHIP_OPTS,
        exportValue: (v) => (v === "custody" ? "امانی" : "مالک: خودمان"),
        render: (v) => (v === "custody" ? <Tag color="orange">امانی</Tag> : <Tag color="blue">مالک: خودمان</Tag>) },
      { title: "مالک", dataIndex: "owner_name", width: 140, sorter: byStr("owner_name"), ...free("owner_name"),
        render: (v, r) => v || (r.ownership === "own" ? <Text type="secondary">خودمان</Text> : <Tag color="red">نامشخص</Tag>) },
      { title: "سریال", dataIndex: "serial_no", width: 130, sorter: byStr("serial_no"), editType: "text",
        render: (v) => (v ? <span style={{ direction: "ltr", display: "inline-block" }}>{v}</span> : "—") },
      { title: "عکس", dataIndex: "has_photo", key: "photo", width: 60, gxFilter: false,
        exportValue: (v) => (v ? "دارد" : ""),
        render: (v, r) => (v ? (
          <Tooltip title="دیدنِ عکسِ سریال">
            <Button size="small" type="text" icon={<CameraFilled style={{ color: "#1F4E79" }} />}
              onClick={() => setPreviewAsset(r)} />
          </Tooltip>) : null) },
      { title: "تعداد", dataIndex: "quantity", width: 75, sorter: byNum("quantity"), editType: "number",
        editRule: (v) => (v == null || v < 0 ? "تعدادِ نامعتبر" : null), render: (v) => faNum(v ?? 0) },
      { title: "مبلغ حدودی (ریال)", dataIndex: "approx_value", width: 140, sorter: byNum("approx_value"),
        editType: "number", render: moneyCell },
      { title: "جمع (ریال)", dataIndex: "total_value", width: 140, sorter: byNum("total_value"), render: moneyCell },
      { title: "تاریخ خرید", dataIndex: "purchase_date", width: 105, sorter: byDate("purchase_date"),
        editType: "date", exportValue: (v) => (v ? toJalali(v) : ""), render: (v) => (v ? toJalali(v) : "—") },
      { title: "تاریخ ورود", dataIndex: "entry_date", width: 105, sorter: byDate("entry_date"),
        editType: "date", exportValue: (v) => (v ? toJalali(v) : ""), render: (v) => (v ? toJalali(v) : "—") },
      { title: "محل نگهداری", dataIndex: "location", width: 140, sorter: byStr("location"), ...free("location"),
        render: (v) => v || "—" },
      { title: "تحویل‌گیرنده / مسئول", dataIndex: "custodian", width: 140, sorter: byStr("custodian"),
        ...free("custodian"), render: (v) => v || "—" },
      { title: "دسته", dataIndex: "category", width: 120, sorter: byStr("category"), ...free("category"),
        render: (v) => v || "—" },
      { title: "وضعیت", dataIndex: "status", width: 115, sorter: byStr("status"), ...free("status"),
        render: (v) => (v ? <Tag color={v === "سالم" ? "green" : "volcano"}>{v}</Tag> : "—") },
      { title: "توضیحات", dataIndex: "notes", width: 180, editType: "textarea", render: (v) => v || "—" },
      ...(canEdit ? [{
        title: "", key: "__act", width: 78, gxFilter: false, gxExport: false, fixed: "left",
        render: (_, r) => (
          <Space size={2}>
            <Tooltip title="ویرایش"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
            <Tooltip title="کپی به ردیفِ تازه"><Button size="small" type="text" icon={<CopyOutlined />} onClick={() => openNew(r)} /></Tooltip>
          </Space>
        ),
      }] : []),
    ];
  }, [opts, canEdit, rowNo]); // eslint-disable-line

  // ─────────────── چاپ و خروجی ───────────────
  const LIST_COLS = [
    { title: "کد", get: (r) => r.code }, { title: "نام", get: (r) => r.name },
    { title: "ویژگی‌ها", get: (r) => r.specs || "" }, { title: "مالک", get: ownerText },
    { title: "سریال", get: (r) => r.serial_no || "" }, { title: "تعداد", get: (r) => r.quantity ?? 0, num: true },
    { title: "مبلغ حدودی", get: (r) => (r.approx_value != null ? formatMoney(r.approx_value) : ""), num: true },
    { title: "جمع", get: (r) => (r.total_value != null ? formatMoney(r.total_value) : ""), num: true },
    { title: "تاریخ خرید", get: (r) => (r.purchase_date ? toJalali(r.purchase_date) : "") },
    { title: "تاریخ ورود", get: (r) => (r.entry_date ? toJalali(r.entry_date) : "") },
    { title: "محل نگهداری", get: (r) => r.location || "" }, { title: "تحویل‌گیرنده", get: (r) => r.custodian || "" },
    { title: "وضعیت", get: (r) => r.status || "" },
  ];
  const totals = (list) => ({
    qty: list.reduce((s, r) => s + (Number(r.quantity) || 0), 0),
    value: list.reduce((s, r) => s + (Number(r.total_value) || 0), 0),
    custody: list.filter((r) => r.ownership === "custody").length,
  });
  const doPrintList = () => {
    if (!target.length) { message.warning("ردیفی برای چاپ نیست"); return; }
    const t = totals(target);
    const ok = printAssetList(target, LIST_COLS, {
      title: "لیست اموال", subtitle: selectedRows.length ? "ردیف‌های انتخاب‌شده" : (own === "custody" ? "اموالِ امانی" : own === "own" ? "مالک: خودمان" : "همه"),
      footer: [["جمع تعداد", faNum(t.qty)], ["جمع مبلغ حدودی (ریال)", formatMoney(t.value)], ["امانی", faNum(t.custody)]],
    });
    if (!ok) message.error("مرورگر پنجرهٔ چاپ را بست — اجازهٔ pop-up را بدهید");
  };

  const sum = totals(view);

  return (
    <div>
      {/* نوارِ کارها — بالای صفحه و چسبان (کاربر: «نباید انتهای صفحه باشد») */}
      <div className="gx-sticky-bar" style={{
        position: "sticky", top: "calc(64px + env(safe-area-inset-top, 0px))", zIndex: 9,
        background: "var(--bg, #eef1f6)", padding: "8px 0", marginBottom: 10,
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
      }}>
        {!isMobile && <Title level={5} style={{ margin: 0, marginInlineEnd: 8 }}>اموال</Title>}
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openNew()} size={isMobile ? "large" : "middle"}
            style={isMobile ? { flex: 2 } : undefined}>افزودنِ اموال</Button>
        )}
        <Tooltip title="اسکنِ لیبل برای پیدا کردنِ اموال (کدِ AM یا سریالِ دستگاه)">
          <Button icon={<ScanOutlined />} size={isMobile ? "large" : "middle"} onClick={() => setScanFor("search")}
            style={isMobile ? { flex: 1 } : undefined}>اسکن</Button>
        </Tooltip>
        {isMobile ? (
          <Dropdown trigger={["click"]} menu={{
            items: [
              { key: "label", icon: <TagsOutlined />, label: `چاپ لیبل (${targetLabel})` },
              { key: "print", icon: <PrinterOutlined />, label: `چاپ لیست (${targetLabel})` },
              { key: "xlsx", icon: <FileExcelOutlined />, label: `اکسل (${targetLabel})` },
              ...(canDelete && selectedRows.length ? [{ key: "del", danger: true, icon: <DeleteOutlined />,
                label: `حذف ${faNum(selectedRows.length)} انتخاب‌شده` }] : []),
              ...(selectedRows.length ? [{ key: "clear", icon: <CloseCircleOutlined />, label: "لغوِ انتخاب" }] : []),
            ],
            onClick: ({ key }) => {
              if (key === "label") (target.length ? setLabelOpen(true) : message.warning("ردیفی نیست"));
              if (key === "print") doPrintList();
              if (key === "xlsx") downloadTableXlsx(tableToPayload(columns, target, "لیست اموال"), "لیست اموال.xlsx")
                .catch(() => message.error("خطا در ساختِ خروجی"));
              if (key === "clear") setSelected([]);
              if (key === "del") Modal.confirm({
                title: `حذفِ ${faNum(selectedRows.length)} ردیفِ انتخاب‌شده`, content: "این کار برگشت‌پذیر نیست.",
                okText: "حذف", cancelText: "انصراف", okButtonProps: { danger: true },
                onOk: () => removeMany(selectedRows.map((r) => r.id)).catch((e) => message.error(errText(e))),
              });
            },
          }}>
            <Button size="large" icon={<MoreOutlined />}>
              {selectedRows.length ? faNum(selectedRows.length) : ""}
            </Button>
          </Dropdown>
        ) : (
          <>
            <Tag color={selectedRows.length ? "geekblue" : "default"} style={{ marginInline: 0 }}>
              {selectedRows.length ? `${faNum(selectedRows.length)} ردیف انتخاب شده` : "چیزی انتخاب نشده — کارها روی کلِ لیستِ فعلی"}
            </Tag>
            {selectedRows.length > 0 && (
              <Button size="small" type="text" icon={<CloseCircleOutlined />} onClick={() => setSelected([])}>لغوِ انتخاب</Button>
            )}
            <Tooltip title={`لیبل برای ${targetLabel}`}>
              <Button icon={<TagsOutlined />} onClick={() => (target.length ? setLabelOpen(true) : message.warning("ردیفی نیست"))}
                style={{ borderColor: "#6d28d9", color: "#6d28d9" }}>
                چاپ لیبل{selectedRows.length ? ` (${faNum(selectedRows.length)})` : ""}
              </Button>
            </Tooltip>
            <Tooltip title={`چاپِ ${targetLabel}`}>
              <Button icon={<PrinterOutlined />} onClick={doPrintList}>
                چاپ لیست{selectedRows.length ? ` (${faNum(selectedRows.length)})` : ""}
              </Button>
            </Tooltip>
            <ExportButton rows={target} columns={columns} filename="لیست اموال" title="لیست اموال"
              subtitle={selectedRows.length ? "ردیف‌های انتخاب‌شده" : undefined}
              label={selectedRows.length ? `اکسلِ انتخاب‌شده‌ها (${faNum(selectedRows.length)})` : "خروجی اکسل"} />
            {canDelete && selectedRows.length > 0 && (
              <Popconfirm title={`حذفِ ${faNum(selectedRows.length)} ردیفِ انتخاب‌شده`} description="این کار برگشت‌پذیر نیست."
                okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }}
                onConfirm={() => removeMany(selectedRows.map((r) => r.id)).catch((e) => message.error(errText(e)))}>
                <Button danger icon={<DeleteOutlined />}>حذف ({faNum(selectedRows.length)})</Button>
              </Popconfirm>
            )}
            <span style={{ flex: 1 }} />
          </>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
          <Segmented value={own} onChange={setOwn} options={[
            { value: "all", label: "همه" }, { value: "own", label: "مالک: خودمان" }, { value: "custody", label: "امانی" }]} />
          <Input.Search allowClear placeholder="جست‌وجو: کد، نام، سریال، محل…" value={q}
            onChange={(e) => setQ(e.target.value)} style={{ width: isMobile ? "auto" : 230, flex: isMobile ? 1 : undefined, minWidth: 140 }} />
          {!isMobile && <Tooltip title="تازه‌سازی"><Button icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>}
        </div>
      </div>

      {isMobile ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: 12.5, marginBottom: 8, padding: "0 4px" }}>
          <span>ردیف: <b>{faNum(view.length)}</b></span>
          <span>تعداد: <b>{faNum(sum.qty)}</b></span>
          <span>امانی: <b>{faNum(sum.custody)}</b></span>
          <span>جمع مبلغ: <b>{formatMoney(sum.value)}</b> ریال</span>
        </div>
      ) : (
      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="تعداد ردیف" value={faNum(view.length)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="جمع تعداد" value={faNum(sum.qty)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="جمع مبلغ حدودی (ریال)" value={formatMoney(sum.value)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="امانی (مالک دیگری)" value={faNum(sum.custody)} /></Card></Col>
      </Row>
      )}

      <Card size="small">
        <EditableTable
          layoutId="assets-list"
          dataSource={view}
          columns={columns}
          rowKey="id"
          loading={loading}
          selectedRowKeys={selected}
          onSelectionChange={setSelected}
          canEditRow={() => canEdit || "دسترسیِ ویرایشِ اموال ندارید"}
          onSaveMany={canEdit ? async (changes) => { await api.post(`${BASE}/bulk-update`, changes); load(); } : undefined}
          onDeleteMany={canDelete ? removeMany : undefined}
          gxSummary={[
            { dataIndex: "name", type: "unique" },
            { dataIndex: "quantity", type: "sum" },
            { dataIndex: "total_value", type: "sum", format: (v) => formatMoney(v) },
          ]}
          gxDefaultHidden={["category", "notes"]}
        />
      </Card>

      {/* ─────────── فرمِ افزودن/ویرایش ─────────── */}
      {/* روی گوشی تمام‌صفحه، ورودی‌های بزرگ و دکمه‌های پایینِ ثابت */}
      <Modal open={modal.open} width={isMobile ? "100vw" : 760}
        title={modal.row ? `ویرایشِ ${modal.row.code}` : "افزودنِ اموال"}
        className={isMobile ? "gx-modal-full" : undefined}
        styles={isMobile ? { body: { maxHeight: "calc(100dvh - 130px)", overflowY: "auto" } } : undefined}
        onCancel={() => setModal({ open: false, row: null })} destroyOnHidden
        footer={(
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => setModal({ open: false, row: null })}>انصراف</Button>
            {!modal.row && <Button loading={saving} onClick={() => submit(true)}>ذخیره و بعدی</Button>}
            <Button type="primary" loading={saving} onClick={() => submit(false)}>ذخیره</Button>
          </Space>
        )}>
        <Form form={form} layout="vertical" size={isMobile ? "large" : "middle"} style={{ direction: "rtl" }}>
          <Row gutter={12}>
            <Col xs={24} md={16}>
              <Form.Item name="name" label="نام" rules={[{ required: true, message: "نام لازم است" }]}>
                <Input placeholder="مثلاً چرخ خیاطی راسته‌دوز ژوکی" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              {/* v2.150.0: کدِ اموال هم اسکنی (کاربر: «بعداً کدِ خودمان را می‌چسبانیم») */}
              <Form.Item label="کد اموال (برچسبِ ما)" extra={modal.row ? undefined : "خالی = خودکار"} required={!!modal.row}>
                <div style={{ display: "flex", gap: 8 }}>
                  <Form.Item name="code" noStyle rules={modal.row ? [{ required: true, message: "کد لازم است" }] : []}>
                    <Input style={{ direction: "ltr", flex: 1, minWidth: 0 }} />
                  </Form.Item>
                  <Tooltip title="اسکنِ کد (بارکد یا متن)">
                    <Button icon={<ScanOutlined />} onClick={() => setScanFor("code")} style={{ flex: "none" }} />
                  </Tooltip>
                </div>
              </Form.Item>
            </Col>
            {/* سریالِ خودِ دستگاه — دستی یا با دوربین */}
            <Col span={24}>
              <Form.Item label="سریالِ دستگاه" style={{ marginBottom: serialDup ? 4 : undefined }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <Form.Item name="serial_no" noStyle>
                    <Input style={{ direction: "ltr", flex: 1, minWidth: 0 }} placeholder={isMobile ? "تایپ یا اسکن" : "تایپ کنید یا با دوربین اسکن کنید"} allowClear />
                  </Form.Item>
                  <Button type="primary" icon={<ScanOutlined />} onClick={() => setScanFor("serial")} style={{ flex: "none" }}>
                    {isMobile ? "اسکن" : "اسکن با دوربین"}
                  </Button>
                </div>
              </Form.Item>
              <Form.Item label="عکسِ سریال / پلاکِ دستگاه" style={{ marginBottom: 12 }}>
                <AssetPhotoField asset={modal.row} value={photo} onChange={setPhoto} />
              </Form.Item>
              {serialDup && (
                <Text type="warning" style={{ display: "block", marginBottom: 12, fontSize: 12.5 }}>
                  ⚠ این سریال قبلاً برای {serialDup.code} — {serialDup.name} ثبت شده است.
                </Text>
              )}
            </Col>
            <Col span={24}>
              <Form.Item name="specs" label="ویژگی‌ها / مشخصات"><Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} /></Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="ownership" label="مالکیت" initialValue="own">
                <Radio.Group optionType="button" options={OWNERSHIP_OPTS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={14}>
              <Form.Item noStyle shouldUpdate={(a, b) => a.ownership !== b.ownership}>
                {({ getFieldValue }) => (
                  <Form.Item name="owner_name" label="نامِ مالک"
                    rules={getFieldValue("ownership") === "custody" ? [{ required: true, message: "برای اموالِ امانی نامِ مالک لازم است" }] : []}>
                    <FreeSelect options={opts.owner_name}
                      placeholder={getFieldValue("ownership") === "custody" ? "صاحبِ کالا" : "خالی = خودمان"} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="quantity" label="تعداد" initialValue={1}><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
            </Col>
            <Col xs={12} md={20}><Form.Item name="approx_value" label="مبلغ حدودیِ واحد (ریال)"><MoneyInput inputMode="numeric" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="purchase_date" label="تاریخ خرید"><JalaliDateInput /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="entry_date" label="تاریخ ورود / تحویل"><JalaliDateInput /></Form.Item></Col>
            <Col xs={24} md={8}>
              <Form.Item name="status" label="وضعیت"><Select allowClear showSearch options={opts.status} /></Form.Item>
            </Col>
            <Col xs={24} md={8}><Form.Item name="location" label="محل نگهداری"><FreeSelect options={opts.location} /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name="custodian" label="تحویل‌گیرنده / مسئول"><FreeSelect options={opts.custodian} /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name="category" label="دسته"><FreeSelect options={opts.category} /></Form.Item></Col>
            <Col span={24}><Form.Item name="notes" label="توضیحات"><Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      <BarcodeScanner open={!!scanFor} onClose={() => setScanFor(null)} onResult={onScanned}
        modeKey={`assets-${scanFor || "x"}`} defaultMode={scanFor === "search" ? "barcode" : "text"}
        title={scanFor === "serial" ? "اسکنِ سریالِ دستگاه" : scanFor === "code" ? "اسکنِ کدِ اموال"
          : "اسکنِ لیبل برای پیدا کردنِ اموال"} />

      <AssetPhotoPreview asset={previewAsset} onClose={() => setPreviewAsset(null)} />

      <LabelModal open={labelOpen} onClose={() => setLabelOpen(false)} rows={target} targetLabel={targetLabel} />
    </div>
  );
}

/** Select تک‌مقداری که مقدارِ تازه هم می‌پذیرد (تایپ + Enter) — برای محل/مسئول/دسته/مالک */
function FreeSelect({ value, onChange, options, placeholder }) {
  return (
    <Select showSearch allowClear mode="tags" value={value ? [value] : []} options={options}
      placeholder={placeholder || "انتخاب یا تایپ"} optionFilterProp="label"
      onChange={(arr) => onChange?.(arr && arr.length ? String(arr[arr.length - 1]).trim() : null)} />
  );
}

/** پنجرهٔ تنظیمِ لیبل — انتخاب‌ها برای دفعهٔ بعد یادش می‌ماند */
function LabelModal({ open, onClose, rows, targetLabel }) {
  const saved = loadOrder("lbl", LABEL_PREF) || {};
  const [size, setSize] = useState(saved.size || "60x40");
  const [fields, setFields] = useState(saved.fields || ["title", "name", "barcode", "code", "serial_no", "owner", "quantity", "date", "location"]);
  const [title, setTitle] = useState(saved.title ?? "اموال قرچک فکتوری");
  const [perQty, setPerQty] = useState(!!saved.perQty);
  const count = perQty ? rows.reduce((s, r) => s + Math.max(1, Number(r.quantity) || 1), 0) : rows.length;
  const go = () => {
    saveOrder("lbl", LABEL_PREF, { size, fields, title, perQty });
    if (count > 2000) { message.error("بیش از ۲۰۰۰ لیبل — تعداد را کم کنید"); return; }
    const ok = printAssetLabels(rows, { size, fields, title, perQuantity: perQty });
    if (!ok) message.error("مرورگر پنجرهٔ چاپ را بست — اجازهٔ pop-up را بدهید");
    else onClose();
  };
  return (
    <Modal open={open} onCancel={onClose} onOk={go} okText={`چاپِ ${faNum(count)} لیبل`} cancelText="انصراف"
      title={`چاپ لیبلِ اموال — ${targetLabel}`} width={560} destroyOnHidden>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, direction: "rtl" }}>
        <div>
          <div style={{ marginBottom: 4, fontWeight: 600 }}>اندازهٔ لیبل</div>
          <Radio.Group value={size} onChange={(e) => setSize(e.target.value)}
            options={LABEL_SIZES.map((s) => ({ value: s.value, label: s.label }))} style={{ display: "flex", flexDirection: "column", gap: 4 }} />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontWeight: 600 }}>روی لیبل چه باشد؟</div>
          <Checkbox.Group value={fields} onChange={setFields} options={LABEL_FIELDS} />
        </div>
        {fields.includes("title") && (
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>عنوانِ بالای لیبل</div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={40} />
          </div>
        )}
        <Checkbox checked={perQty} onChange={(e) => setPerQty(e.target.checked)}>
          برای هر قلم به اندازهٔ «تعداد» لیبل بزن (مثلاً ۵ صندلی ⇒ ۵ لیبل با «۱ از ۵»…)
        </Checkbox>
        <Text type="secondary" style={{ fontSize: 12 }}>
          در پنجرهٔ چاپ: «Margins: None» و اندازهٔ کاغذِ چاپگرِ لیبل را انتخاب کنید. برای PDF «Save as PDF» را بزنید.
        </Text>
      </div>
    </Modal>
  );
}
